// Address search on a Bootstrap input, backed by the NEW Places API.
//
// The legacy google.maps.places.Autocomplete widget is closed to projects
// created after March 2025, so this uses AutocompleteSuggestion +
// Place.fetchFields with its own suggestion dropdown: type a few characters,
// pick a suggestion, and onSelect receives the parsed parts to fill the
// sibling city / province / postal-code fields. While the Maps script has not
// loaded (or fails, or the new classes are unavailable), it behaves as a
// plain Form.Control, so the manual-entry path always works.
//
// Cost notes (decided 2026-09): requests share an AutocompleteSessionToken
// that fetchFields closes, so a completed pick bills as one Place Details
// Essentials call. fetchFields is pinned to addressComponents only; adding
// fields can move the call to the Pro SKU at three times the price. A 300ms
// debounce and a 3-character minimum keep abandoned-search keystrokes cheap.

import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAP_LIBRARIES } from "./googleMapsConfig";
import "../Accounts/acc-list.css";

const componentOf = (components, type, useShort = false) => {
  const match = (components || []).find((c) => (c.types || []).includes(type));
  if (!match) return "";
  return (useShort ? match.shortText : match.longText) || "";
};

// New-API Place.addressComponents -> the app's address shape.
export const parseAddressComponents = (components) => {
  const streetNumber = componentOf(components, "street_number");
  const route = componentOf(components, "route");
  return {
    address1: [streetNumber, route].filter(Boolean).join(" "),
    address2:
      componentOf(components, "sublocality") ||
      componentOf(components, "sublocality_level_1") ||
      componentOf(components, "neighborhood"),
    city:
      componentOf(components, "locality") ||
      componentOf(components, "postal_town") ||
      componentOf(components, "administrative_area_level_2"),
    province: componentOf(components, "administrative_area_level_1"),
    postalCode: componentOf(components, "postal_code"),
  };
};

export default function AddressAutocomplete({
  onSelect,
  onChange,
  value,
  ...inputProps
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const closeList = () => {
    setSuggestions([]);
    setActiveIndex(-1);
  };

  // Click or tap outside the input closes the list.
  useEffect(() => {
    const onDocPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeList();
    };
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const fetchSuggestions = async (input) => {
    const places = window.google?.maps?.places;
    if (!places?.AutocompleteSuggestion) return;
    if (!sessionRef.current && places.AutocompleteSessionToken) {
      sessionRef.current = new places.AutocompleteSessionToken();
    }
    try {
      const { suggestions: results } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionRef.current || undefined,
          includedRegionCodes: ["za"],
        });
      setSuggestions(results || []);
      setActiveIndex(-1);
    } catch {
      closeList();
    }
  };

  const handleChange = (e) => {
    onChange?.(e);
    const text = e.target.value || "";
    clearTimeout(debounceRef.current);
    if (text.trim().length < 3) {
      closeList();
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(text.trim()), 300);
  };

  const pick = async (suggestion) => {
    closeList();
    try {
      const place = suggestion?.placePrediction?.toPlace();
      if (!place) return;
      await place.fetchFields({ fields: ["addressComponents"] });
      // fetchFields ends the billing session; the next keystroke starts a
      // fresh one.
      sessionRef.current = null;
      const parsed = parseAddressComponents(place.addressComponents);
      if (parsed.address1 || parsed.city || parsed.postalCode) {
        onSelect(parsed);
      }
    } catch {
      // Leave whatever the user typed in place; manual entry still works.
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Enter confirms a suggestion; it must never submit the form.
      e.preventDefault();
      if (suggestions.length > 0) pick(suggestions[Math.max(activeIndex, 0)]);
      return;
    }
    if (suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      closeList();
    }
  };

  if (!isLoaded || !GOOGLE_MAPS_API_KEY) {
    return <Form.Control value={value} onChange={onChange} {...inputProps} />;
  }

  return (
    <div ref={wrapRef} className="position-relative">
      <Form.Control
        value={value}
        {...inputProps}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={suggestions.length > 0}
        aria-autocomplete="list"
      />
      {suggestions.length > 0 && (
        <div className="address-suggest shadow-sm" role="listbox">
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={s?.placePrediction?.placeId || i}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-suggest-item ${i === activeIndex ? "active" : ""}`}
              // preventDefault keeps focus in the input so blur handlers on
              // the form do not fire before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
            >
              {s?.placePrediction?.text?.text || ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
